'use strict';

/**
 * SFRA Cart model
 * JSDoc with some common properties.
 * More properties: /models/cart.js
 * @typedef {object} Cart
 * @property {object} [actionUrls] - Useful URLs
 * @property {object} actionUrls.removeProductLineItemUrl - Remove line endpoint
 * @property {object} actionUrls.updateQuantityUrl - Update quantity endpoint
 * @property {object[]} [approachingDiscounts]
 * @property {string} approachingDiscounts.discountMsg
 * @property {number|null} cartMaxSKUAmountExceeded
 * @property {number|null} cartMaxPriceExceeded
 * @property {boolean} [hasBonusProduct] True if cart has bonus products
 * @property {ProductLineItem[]} items - Items added to cart
 * @property {number} numItems - Number of items in the cart
 * @property {number} [numOfShipments] Number of shipments
 * @property {object} resources
 * @property {string} resources.emptyCartMsg
 * @property {string} resources.giftMessage
 * @property {string} resources.isGift
 * @property {string} resources.maxPriceExceededMsg
 * @property {string} resources.maxSKUAmountExceededMsg
 * @property {string} resources.minicartCountOfItems
 * @property {string} resources.numberOfItems
 * @property {Shipping[]} [shipments] Cart shipments data
 * @property {Totals} [totals] - Cart price totals
 * @property {string} uuid Cart Unique identifier
 * @property {boolean} [valid]
 */

/**
 * Product related info returned by server for an analytics event
 * @typedef {object} DataLayerCartActionOnProduct
 * @property {object} cart - Cart info
 * @property {ProductFieldObject.ProductFieldObject[]} cart.items - The product
 * field objects of all the items in cart
 * @property {string} cartAction - addToCart or removeFromCart
 * @property {string} currencyCode - Currency code
 * @property {ProductFieldObject.ProductFieldObject} productFieldObject - The
 * product field object
 */

/**
 * SFRA Order model
 * JSDoc with some common properties.
 * More properties: /models/order.js
 * @typedef {object} Order
 * @property {Billing} billing - Order billing data
 * @property {ProductLineItems} items - Items added to order
 * @property {Shipping[]} shipping - Order shipping data
 * @property {Totals} totals - Order price totals
 */

/**
 * It refers to the model defined by SF for the items stored in the SFRA cart.
 * JSDoc with some common properties.
 * More properties: /models/productLineItem/productLineItem.js
 * @typedef {Object} ProductLineItem
 * @property {Boolean} available - true if the line item is available
 * @property {string} id - the ID of the variant added to cart
 * @property {string} parentCategoryName - line item parent category name
 * @property {Object} price - line item price object
 * @property {Number|null} price.discount - line item price discount
 * @property {Object|null} price.list - line item price list
 * @property {string} price.list.currency - line item price list currency
 * @property {string} price.list.decimalPrice - line item price list decimal price
 * @property {string} price.list.formatted - line item price list formatted value
 * @property {Number} price.list.value - line item price list value
 * @property {Object} price.sales - line item price sales
 * @property {string} price.sales.currency - line item price sales currency
 * @property {string} price.sales.decimalPrice - line item price sales decimal price
 * @property {string} price.sales.formatted - line item price sales formatted value
 * @property {Number} price.sales.value - line item price sales value
 * @property {Object} priceTotal - total price
 * @property {Number} priceTotal.floatPrice - numeric price
 * @property {string} priceTotal.price - price with currency
 * @property {string} priceTotal.renderedPrice - HTML of the price to show
 * @property {string} productName - the name of the product
 * @property {Number} quantity - number of units added
 * @property {Object} unitPrice - price by unit
 * @property {Number} unitPrice.floatPrice - numeric price
 * @property {string} unitPrice.price - price with currency
 * @property {string} unitPrice.renderedPrice - HTML of the price to show
 * @property {string} uuid - unique ID of the product
 * @property {string} UUID - unique ID of the line item
 * @property {Object[]} variationAttributes - Product variation attributes
 * @property {string} variationAttributes.attributeId - Product variation attribute id
 * @property {string} variationAttributes.displayName - Product variation attribute display name
 * @property {string} variationAttributes.displayValue - Product variation attribute display value
 * @property {string} variationAttributes.id - Product variation attribute id
 * @property {string} variationAttributes.value - Product variation attribute value
 */

/**
 * It refers to the model defined by SF for the items stored in the SFRA cart.
 * JSDoc with some common properties.
 * More properties: /models/productLineItems.js
 * @typedef {Object} ProductLineItems
 * @property {ProductLineItem[]} items
 * @property {number} totalQuantity
 */

/**
 * SFRA Totals model
 * JSDoc with some common properties.
 * More properties: /models/totals.js
 * @typedef {object} Totals
 * @property {object[]} [discounts]
 * @property {boolean} discounts.applied
 * @property {string} discounts.couponCode
 * @property {object[]} discounts.relationship
 * @property {string} discounts.relationship.callOutMsg
 * @property {string} discounts.type
 * @property {string} discounts.UUID
 * @property {boolean} discounts.valid
 * @property {string} [discountsHtml]
 * @property {string} floatTotal
 * @property {string} grandTotal The grand total price gross of tax for
 * LineItemCtnr, in purchase currency. Total prices represent the sum of product
 * prices, services prices and adjustments.
 * @property {boolean} [isFree]
 * @property {string} netGrandTotal
 * @property {object} orderLevelDiscountTotal
 * @property {string} orderLevelDiscountTotal.formatted
 * @property {string} orderLevelDiscountTotal.value
 * @property {null} [priceAdjustments]
 * @property {object} shippingLevelDiscountTotal
 * @property {string} shippingLevelDiscountTotal.formatted
 * @property {string} shippingLevelDiscountTotal.value
 * @property {string} subTotal
 * @property {string} totalShippingCost The shipping total price. If the line
 * item container is based on net pricing the shipping total net price is
 * returned. If the line item container is based on gross pricing the shipping
 * total gross price is returned.
 * @property {string} totalTax The grand total tax for LineItemCtnr, in purchase
 * currency. Total prices represent the sum of product prices, services prices
 * and adjustments.
 */

/**
 * @typedef {Object[]} doubleLookProductStockUpdateData
 * @property {string} doubleLookProductStockUpdateData.productId
 * @property {number} doubleLookProductStockUpdateData.units
 */

module.exports = {};
